import { StrictMode } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'

import { CacheProvider, ThemeProvider } from '@dhl-ui/connector'
import { I18nProvider } from '@dhl/i18n'
import { setupLocalXsrfRequestInterceptor, useSetupLocalXsrfRequestInterceptor } from '@dhl/receiver-axios'
import { useDeviceDetect } from '@dhl/theme'
import { initDataLayer } from '@dhl/third-party'
import { VersionProvider } from '@dhl/version-provider'
import { isNative } from '@dhl/webview'

import { StartUp } from '~/StartUp'
import { AccountNativeBridge } from './AccountNativeBridge'
import { getConfig } from './config'
import { useSetupRequestInterceptor } from './interceptor'
import { queryClient } from './query-client'
import { Routes } from './Routes'
import { ThemeOverride } from './ThemeOverride'
import { useAuthRedirect } from './useAuthRedirect'
import { useReferrer } from './useReferrer'

setupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)

initDataLayer()

const InnerApp = () => {
  useReferrer()
  useAuthRedirect()
  useSetupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)
  useSetupRequestInterceptor()
  const { isMobile } = useDeviceDetect()
  const isNativeApp = isNative()

  const { basePath, cspNonce, version } = getConfig()
  const baseNameWithoutTrailingSlash = basePath.slice(0, -1)

  return (
    <CacheProvider nonce={cspNonce}>
      <ThemeProvider
        options={{
          variant: isMobile ? 'mobile' : 'desktop',
          disablePtr: true,
          disableActiveState: isNativeApp,
          userSelect: isNativeApp ? 'none' : undefined,
        }}
      >
        <ThemeOverride>
          <I18nProvider>
            <BrowserRouter
              basename={baseNameWithoutTrailingSlash}
              future={{
                v7_relativeSplatPath: true,
                v7_startTransition: false,
              }}
            >
              <VersionProvider url={`${basePath}version.json`} version={version}>
                <AccountNativeBridge>
                  <StartUp>
                    <StrictMode>
                      <Routes />
                    </StrictMode>
                  </StartUp>
                </AccountNativeBridge>
              </VersionProvider>
            </BrowserRouter>
          </I18nProvider>
        </ThemeOverride>
      </ThemeProvider>
    </CacheProvider>
  )
}

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <InnerApp />
  </QueryClientProvider>
)
